import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import Icon from '../../common/Icons';
import clinicalReportsService from '../../../services/clinicalReportsService';
import CompanyService from '../../../services/companyService';
import SiteService from '../../../services/siteService';
import { HEAD_DOCTOR_COPY } from '../../head-doctor/headDoctorCopy';
import { MAJOR_COPY } from '../../major/majorCopy';
import { AdminPageHeader } from '../ui';
import ReportsFilterBar from '../../head-doctor/reports/ReportsFilterBar';
import ReportsSubNav from '../../head-doctor/reports/ReportsSubNav';
import { ClinicalReportsContext } from '../../clinical-reports/ClinicalReportsContext';
import { REPORTS_PERIOD_PRESETS } from '../../head-doctor/reports/reportsNavConfig';
import { todayYmd, daysAgoYmd } from '../../../utils/appTimezone';
import { ADMIN_CLINICAL_REPORT_PATHS } from './adminReportCatalog';
import { ADMIN_CLINIQUE_NAV, ADMIN_NURSING_ANALYTICS_NAV } from './adminAnalyticsNav';
import AdminReportsDomainNav from './AdminReportsDomainNav';
import '../../head-doctor/HeadDoctorDashboard.css';
import '../../head-doctor/reports/charts/chartTheme';
import './AdminClinicalAnalyticsLayout.css';

const CLINICAL_COPY = {
  ...HEAD_DOCTOR_COPY.REPORTS,
  BADGE: 'Administration',
  TITLE: 'Rapports médicaux',
  DESCRIPTION:
    'Statistiques cliniques institutionnelles — même modèle que le médecin chef, tous sites.'
};

function detectActivePreset(from, to) {
  const today = todayYmd();
  if (to !== today) return null;
  const match = REPORTS_PERIOD_PRESETS.find((preset) => from === daysAgoYmd(preset.days));
  return match?.key || null;
}

export default function AdminClinicalAnalyticsLayout({ mode = 'clinical' }) {
  const { toast } = useOutletContext() || { toast: () => {} };
  const COPY = mode === 'nursing' ? MAJOR_COPY.REPORTS : CLINICAL_COPY;
  const [from, setFrom] = useState(daysAgoYmd(30));
  const [to, setTo] = useState(todayYmd());
  const [siteFilter, setSiteFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [companies, setCompanies] = useState([]);
  const [workSites, setWorkSites] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [companiesRes, sitesRes] = await Promise.all([
          CompanyService.getActiveCompanies(),
          SiteService.getActiveSites()
        ]);
        if (cancelled) return;
        setCompanies(companiesRes.success && Array.isArray(companiesRes.data) ? companiesRes.data : []);
        setWorkSites(sitesRes.success && Array.isArray(sitesRes.data) ? sitesRes.data : []);
      } catch {
        if (!cancelled) {
          setCompanies([]);
          setWorkSites([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activePreset = useMemo(() => detectActivePreset(from, to), [from, to]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { from, to, limit: 20 };
      if (siteFilter) filters.site_id = parseInt(siteFilter, 10);
      if (companyFilter) filters.company_id = parseInt(companyFilter, 10);
      const res = await clinicalReportsService.getOverview(filters);
      if (res.success) {
        setData(res.data);
      } else {
        setData(null);
        toast('danger', COPY.ERROR_LOAD);
      }
    } catch {
      setData(null);
      toast('danger', COPY.ERROR_LOAD);
    } finally {
      setLoading(false);
    }
  }, [from, to, siteFilter, companyFilter, COPY.ERROR_LOAD, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePreset = useCallback((preset) => {
    setTo(todayYmd());
    setFrom(daysAgoYmd(preset.days));
  }, []);

  const siteLabel = useMemo(() => {
    if (!siteFilter) return COPY.FILTER_ALL_SITES;
    const site = workSites.find((s) => String(s.id) === String(siteFilter));
    return site?.name || data?.filters?.site_name || COPY.FILTER_SITE;
  }, [siteFilter, workSites, data?.filters?.site_name, COPY.FILTER_ALL_SITES, COPY.FILTER_SITE]);

  const companyLabel = useMemo(() => {
    if (!companyFilter) return COPY.FILTER_ALL_COMPANIES;
    const company = companies.find((c) => String(c.id) === String(companyFilter));
    return company?.name || data?.filters?.company_name || COPY.FILTER_COMPANY;
  }, [
    companyFilter,
    companies,
    data?.filters?.company_name,
    COPY.FILTER_ALL_COMPANIES,
    COPY.FILTER_COMPANY
  ]);

  const periodLabel = data?.range
    ? `${data.range.from} → ${data.range.to}`
    : `${from} → ${to}`;

  const value = useMemo(
    () => ({
      data,
      loading,
      error: null,
      summary: data?.summary || {},
      copy: COPY,
      refresh: load,
      siteLabel,
      companyLabel,
      periodLabel,
      reportPaths: mode === 'clinical' ? ADMIN_CLINICAL_REPORT_PATHS : null,
      activeAdvancedCount: 0,
      hasActiveAdvancedFilters: false,
      clearAdvancedFilters: null,
      reportFilters: {
        from,
        to,
        site_id: siteFilter ? parseInt(siteFilter, 10) : null,
        company_id: companyFilter ? parseInt(companyFilter, 10) : null,
        limit: 10000
      }
    }),
    [
      data,
      loading,
      load,
      siteLabel,
      companyLabel,
      periodLabel,
      from,
      to,
      siteFilter,
      companyFilter,
      mode,
      COPY
    ]
  );

  const navItems = mode === 'nursing' ? ADMIN_NURSING_ANALYTICS_NAV : ADMIN_CLINIQUE_NAV;
  const navIcons = mode === 'nursing' ? MAJOR_COPY.NAV : HEAD_DOCTOR_COPY.NAV;

  const title =
    mode === 'nursing' ? 'Rapports infirmerie' : CLINICAL_COPY.TITLE;
  const description =
    mode === 'nursing'
      ? 'Journal et statistiques du poste infirmier (modèle major), tous sites.'
      : CLINICAL_COPY.DESCRIPTION;

  return (
    <ClinicalReportsContext.Provider value={value}>
      <div className="adm-analytics-page hd-reports-page">
        <AdminPageHeader badge="Administration" title={title} description={description} />
        <AdminReportsDomainNav />

        <section className={`hd-reports-shell mb-4${loading ? ' is-loading' : ''}`} aria-busy={loading}>
          <ReportsFilterBar
            from={from}
            to={to}
            siteFilter={siteFilter}
            companyFilter={companyFilter}
            workSites={workSites}
            companies={companies}
            loading={loading}
            activePreset={activePreset}
            allowAllSites
            showSiteFilter
            onFromChange={setFrom}
            onToChange={setTo}
            onSiteChange={setSiteFilter}
            onCompanyChange={setCompanyFilter}
            onPreset={handlePreset}
            onRefresh={load}
          />

          <div className="hd-reports-context">
            <span className="hd-reports-context__label">{COPY.CONTEXT_PREFIX}</span>
            <div className="hd-reports-context__chips">
              <span className="hd-reports-context-chip hd-reports-context-chip--site">
                <Icon name="building" size={14} aria-hidden />
                <strong>{siteLabel}</strong>
              </span>
              <span className="hd-reports-context-chip hd-reports-context-chip--company">
                <Icon name="users" size={14} aria-hidden />
                <strong>{companyLabel}</strong>
              </span>
              <span className="hd-reports-context-chip">
                <Icon name="calendar" size={14} aria-hidden />
                <strong>{periodLabel}</strong>
              </span>
            </div>
          </div>

          <ReportsSubNav copy={COPY} navItems={navItems} navIcons={navIcons} />

          <div className="hd-reports-outlet">
            <Outlet context={{ toast }} />
          </div>
        </section>
      </div>
    </ClinicalReportsContext.Provider>
  );
}

export function AdminNursingAnalyticsLayout() {
  return <AdminClinicalAnalyticsLayout mode="nursing" />;
}
